import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Button,
  Center,
  Group,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { useDocumentTitle } from "@mantine/hooks";
import {
  IconAt,
  IconExclamationCircle,
  IconInfoCircle,
  IconLock,
  IconUserCheck,
} from "@tabler/icons-react";
import { motion } from "framer-motion";
import { jwtDecode } from "jwt-decode";
import i18n from "src/i18n";
import { z } from "zod";

import { CustomPhoneInput } from "@components/CustomPhoneInput";
import { EnvAlert } from "@components/EnvAlert";
import { LanguageToggle } from "@components/LanguageToggle";

import { capitalizeFirstLetter } from "@utils/capitalizeFirstLetter";
import { startConfetti } from "@utils/confetti";
import { showErrorNotification } from "@utils/showNotification";

import { PAGE_TITLE } from "@constants/index";

import { useCreateUser } from "@hooks/mutations/user/useCreateUser";
import { useCountDown } from "@hooks/useCountDown";

import Logo from "@assets/misc/logo.webp";

import { StyledRegister } from "./Register.styled";


const schema = () =>
  z
    .object({
      firstName: z
        .string()
        .min(1, i18n.t("first-name-required"))
        .min(3, i18n.t("first-name-min-3-chars")),
      surname: z
        .string()
        .min(1, i18n.t("surname-required"))
        .min(3, i18n.t("surname-min-3-chars")),
      username: z
        .string()
        .min(1, i18n.t("username-required"))
        .min(3, i18n.t("username-min-3-chars")),
      email: z
        .string()
        .email(i18n.t("email-invalid"))
        .min(1, i18n.t("email-required")),
      phoneNumber: z
        .string()
        .min(10, i18n.t("invalid-number"))
        .optional()
        .or(z.literal("")),
      password: z.string().min(5, i18n.t("password-min-5-chars")),
      confirmPassword: z.string().min(1, i18n.t("confirm-password-required")),
    })
    .refine(data => data.password === data.confirmPassword, {
      message: i18n.t("passwords-mismatch"),
      path: ["confirmPassword"], // path of error
    });

type Inputs = z.infer<ReturnType<typeof schema>>;

const COUNTDOWN = 7;

export const Register = () => {
  const { t } = useTranslation();
  useDocumentTitle(`${t("register")} • ${PAGE_TITLE}`);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const form = useForm({
    mode: "uncontrolled",
    validate: zodResolver(schema()),
    initialValues: {
      firstName: "",
      surname: "",
      username: "",
      email: "",
      phoneNumber: "",
      password: "",
      confirmPassword: "",
    },
    transformValues: values => ({
      ...values,
      fullName: `${capitalizeFirstLetter(
        values.firstName
      )} ${capitalizeFirstLetter(values.surname)}`,
    }),
  });

  const [step, setStep] = useState(1);
  const [previousStep, setPreviousStep] = useState(step);
  const delta = step - previousStep;
  const [isTokenExpired, setIsTokenExpired] = useState(false);
  const [isTokenMissing, setIsTokenMissing] = useState(false);
  const [isTokenError, setIsTokenError] = useState(false);
  const [roles, setRoles] = useState<string[]>([]);
  const { count, setStartCounting } = useCountDown({ seconds: COUNTDOWN });

  const {
    mutate: createUser,
    isLoading: isCreateUserLoading,
    isSuccess: isCreateUserSuccess,
  } = useCreateUser();

  const handleFormSubmit = (values: Inputs) => {
    if (isTokenExpired) return;
    if (step === 2) {
      // console.log("submitting...", values);
      const newForm = {
        fullName: form.getTransformedValues().fullName,
        username: values.username,
        roles,
        email: values.email,
        phone_number: values.phoneNumber,
        password: values.confirmPassword,
      };
      createUser(newForm, {
        onSuccess: () => {
          // Start countdown
          setStartCounting(true);

          // Start confetti
          startConfetti();

          setTimeout(() => {
            navigate("/", { state: { username: form.getValues().username } });
          }, 1000 * COUNTDOWN);
        },
      });
    }
  };

  const handleNext = () => {
    // console.log("inside next func");
    if (isTokenExpired || isTokenError || isTokenMissing) return;
    const isValidated = [
      form.validateField("firstName").hasError,
      form.validateField("surname").hasError,
      form.validateField("username").hasError,
      form.validateField("email").hasError,
      form.validateField("phoneNumber").hasError,
    ];
    if (isValidated.every(item => item === false)) {
      setPreviousStep(step);
      setStep(prev => prev + 1);
    }
  };

  useEffect(() => {
    if (searchParams.toString()) {
      // console.log(searchParams.toString());
      // console.log(searchParams.toString().split("=")[1]);
      try {
        const res: any = jwtDecode(searchParams.toString().split("=")[1]);
        // console.log("Token expires on:", new Date(res.exp * 1000));

        if (res.exp * 1000 < new Date().getTime()) {
          setIsTokenExpired(true);
        } else {
          setIsTokenExpired(false); // Just to be safe
          form.setValues({ email: (res as any).email });
          setRoles(res.roles);
        }
      } catch (error) {
        console.error("[Register]", error);
        setIsTokenError(true);
        showErrorNotification({
          title: "invalid-token",
          message: "invite-link-error",
        });
      }
    } else {
      setIsTokenMissing(true); // Just to be safe
    }
  }, [searchParams]);

  return (
    <StyledRegister>
      <LanguageToggle />

      <div className="content">
        <div className="logo">
          <img src={Logo} alt="La Ruche Health" />
        </div>

        {!isCreateUserSuccess ? (
          <>
            <p className="heading">{t("registration-portal")}</p>

            <EnvAlert />
            {isTokenExpired && (
              <Alert
                mt={"md"}
                variant="light"
                color="red"
                icon={<IconExclamationCircle />}>
                <Text fz={14} lh={1.5} c="red">
                  {t("invite-link-expired")}
                </Text>
              </Alert>
            )}
            {isTokenMissing && (
              <Alert
                mt={"md"}
                variant="light"
                color="red"
                icon={<IconExclamationCircle />}>
                <Text fz={14} lh={1.5} c="red">
                  {t("invite-link-missing")}
                </Text>
              </Alert>
            )}

            {step === 1 ? (
              <motion.form
                initial={{
                  x: delta >= 0 ? "50%" : "-50%",
                  opacity: 0,
                }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="form"
                //! OnSubmit function does not work for some reason, even when error does
                // onSubmit={form.onSubmit(
                //   () => handleNext(),
                //   errors => {
                //     const firstErrorPath = Object.keys(errors)[0];
                //     form.getInputNode(firstErrorPath)?.focus();
                //   }
                // )}
              >
                <div className="form-group">
                  <TextInput
                    label={t("first-name")}
                    withAsterisk
                    placeholder={t("eg", { example: "John" })}
                    {...form.getInputProps("firstName")}
                    key={form.key("firstName")}
                  />
                  <TextInput
                    label={t("surname")}
                    withAsterisk
                    placeholder={t("eg", { example: "Doe" })}
                    {...form.getInputProps("surname")}
                    key={form.key("surname")}
                  />
                </div>
                <Tooltip
                  label={t("dont-forget-username")}
                  withArrow
                  events={{ focus: true, hover: false, touch: false }}>
                  <TextInput
                    label={t("username")}
                    description={t("username-reason")}
                    withAsterisk
                    placeholder={t("eg", { example: "johndoe123" })}
                    {...form.getInputProps("username")}
                    key={form.key("username")}
                  />
                </Tooltip>
                <TextInput
                  label={t("email")}
                  withAsterisk
                  readOnly
                  leftSection={<IconAt size={16} />}
                  placeholder={t("eg", { example: "johndoe@gmail.com" })}
                  rightSection={
                    <Tooltip
                      withArrow
                      label={t("email-immutable")}
                      events={{ focus: true, hover: true, touch: true }}>
                      <IconInfoCircle size={16} />
                    </Tooltip>
                  }
                  {...form.getInputProps("email")}
                  key={form.key("email")}
                />
                
                <div className="form-group" style={{ marginBottom: "1rem" }}>
                  <label 
                    htmlFor="phone-input" 
                    className="mantine-InputWrapper-label mantine-TextInput-label"
                    style={{ 
                      display: "inline-block", 
                      fontSize: "14px", 
                      fontWeight: 500, 
                      color: "var(--mantine-color-text)", 
                      marginBottom: "4px" 
                    }}
                  >
                    {t("phone-number")}
                  </label>
                  <CustomPhoneInput
                    id="phone-input"
                    phone={form.getValues().phoneNumber}
                    setPhone={(val) => form.setFieldValue("phoneNumber", val)}
                  />
                  {form.errors.phoneNumber && (
                    <div style={{ color: "var(--mantine-color-error)", fontSize: "12px", marginTop: "4px" }}>
                      {form.errors.phoneNumber}
                    </div>
                  )}
                </div>

                <Button
                  mt={"lg"}
                  onClick={handleNext}
                  // type="submit"
                  disabled={isTokenExpired || isTokenMissing || isTokenError}>
                  {t("next")}
                </Button>
              </motion.form>
            ) : null}

            {step === 2 ? (
              <motion.form
                initial={{
                  x: delta >= 0 ? "50%" : "-50%",
                  opacity: 0,
                }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="form"
                onSubmit={form.onSubmit(
                  values => handleFormSubmit(values),
                  errors => {
                    const firstErrorPath = Object.keys(errors)[0];
                    form.getInputNode(firstErrorPath)?.focus();
                  }
                )}>
                <PasswordInput
                  label={t("password")}
                  withAsterisk
                  leftSection={<IconLock size={16} />}
                  {...form.getInputProps("password")}
                  key={form.key("password")}
                />
                <PasswordInput
                  label={t("confirm-password")}
                  withAsterisk
                  leftSection={<IconLock size={16} />}
                  {...form.getInputProps("confirmPassword")}
                  key={form.key("confirmPassword")}
                />
                <Group mt="auto" justify="end" align="end" flex={1}>
                  <Button
                    variant="outline"
                    disabled={
                      isTokenExpired ||
                      isTokenMissing ||
                      isCreateUserLoading ||
                      isTokenError
                    }
                    onClick={() => {
                      setPreviousStep(step);
                      setStep(prev => prev - 1);
                    }}>
                    {t("previous")}
                  </Button>
                  <Button
                    type="submit"
                    disabled={isTokenExpired || isTokenMissing || isTokenError}
                    loading={isCreateUserLoading}>
                    {t("submit")}
                  </Button>
                </Group>
              </motion.form>
            ) : null}
          </>
        ) : (
          <Stack mt="xl" gap="0">
            <Center>
              <Group
                justify="center"
                align="center"
                bg="green.0"
                style={{ borderRadius: "50%", height: 80, width: 80 }}>
                <IconUserCheck
                  size={35}
                  color={"var(--mantine-color-green-6)"}
                  stroke={1.5}
                />
              </Group>
            </Center>
            <Stack gap="xs" mt="md">
              <Title order={1} fw={500} fz={20} mt="md">
                {t("welcome-aboard")}
              </Title>
              <Text c="dimmed" fz={15} lh={1.5}>
                {t("redirected-in-x", { count })}
              </Text>
            </Stack>
          </Stack>
        )}
      </div>
    </StyledRegister>
  );
};
